import { useAuthQuery } from '@/api/auth-query';
import { authQueryKeys, orgQueryKeys } from '@/config/queryKeys';
import {
  NAV_LOCK_DURATION_MS,
  ORGS_QUERY_GC_TIME,
  ORGS_QUERY_STALE_TIME,
  POST_LOGIN_MAX_ATTEMPTS,
  POST_LOGIN_MAX_ATTEMPTS_EXTENDED,
  POST_LOGIN_POLL_INTERVAL_MS,
  RECENT_LOGOUT_WINDOW_MS,
  isPostLoginCallback,
  server,
} from '@/constants';
import { useLogout } from '@/hooks';
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { useAppStore } from '@/stores/app.ts';
import type { Org } from '@/types';
import { FullLoading } from '@components/Loaders';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

export const Route = createFileRoute('/')({
  component: () => {
    // Handle post-login redirect
    usePostLoginRedirect();

    // Detect when we've just come back from a server-side login redirect.
    // On the very first /auth/me call the session cookie may not yet be
    // visible (timing / cookie-propagation race), so we give the query a
    // one-shot retry before treating null as "unauthenticated".
    const postLoginCallback = isPostLoginCallback(window.location.pathname, window.location.search);

    const queryClient = useQueryClient();
    // Use the project auth query helper which intentionally uses fetch directly
    // to avoid auth redirect loops that can be triggered by ApiClient's global 401 handling.
    const { data: authData, error, isSuccess } = useAuthQuery({ suspense: true });
    const { data, isPending } = useQuery<Org[]>({
      enabled: !!authData?.id && isSuccess,
      queryKey: orgQueryKeys.list(),
      queryFn: fetchOrganizations,
      staleTime: ORGS_QUERY_STALE_TIME,
      gcTime: ORGS_QUERY_GC_TIME,
    });
    const { t } = useTranslation('login');
    const logout = useLogout();

    const { setSelectedOrg, selectedOrganization, selectedOrgId, setSelectedOrgId } = useAppStore(
      useShallow(state => ({
        setSelectedOrg: state.setSelectedOrganization,
        selectedOrganization: state.selectedOrganization,
        selectedOrgId: state.selectedOrgId,
        setSelectedOrgId: state.setSelectedOrgId,
      })),
    );
    // If 401/403, go login.
    // Skip the redirect on the post-login callback URL (?login=success) so we
    // don't bounce back to /login before the session cookie has propagated.
    useEffect(() => {
      // If we get an explicit FORBIDDEN from the auth check, perform logout mutation
      // The actual navigation to /login will be handled by the component render (<Navigate />)
      if (error?.message === 'FORBIDDEN') {
        logout.mutate();
      }
      // For unauthenticated state, avoid programmatic navigate here — the component
      // render will return <Navigate to="/login" /> which avoids concurrent transitions.
    }, [error, logout]);

    // When landing on /?login=success and auth initially returns null (401
    // before cookie propagation), schedule a one-shot refetch so the query
    // picks up the now-valid session without a full round-trip to /login.
    const didScheduleRefetch = useRef(false);
    useEffect(() => {
      if (!postLoginCallback || authData || didScheduleRefetch.current) return undefined;

      didScheduleRefetch.current = true;

      // Actively poll the server /auth/me endpoint until the session cookie
      // is visible (200) or until we've retried enough times. This avoids
      // racing the router into an unauthenticated redirect before the cookie
      // is set by the server after a redirect-based login flow.
      const RETRY_INTERVAL_MS = POST_LOGIN_POLL_INTERVAL_MS;
      // Base attempts; extend if we detect a recent logout (quick re-login)
      let MAX_ATTEMPTS = POST_LOGIN_MAX_ATTEMPTS; // ~14s total
      try {
        const lastLogout = Number(sessionStorage.getItem('__last_logout_ts')) || 0;
        if (Date.now() - lastLogout < RECENT_LOGOUT_WINDOW_MS) {
          // If the user logged out within the last 10s, they're likely to
          // re-login quickly; extend polling window.
          MAX_ATTEMPTS = POST_LOGIN_MAX_ATTEMPTS_EXTENDED;
        }
      } catch {}
      let attempts = 0;
      let stopped = false;
      let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;

      const doPoll = async () => {
        if (stopped) return;
        attempts += 1;
        try {
          const res = await fetch(`${server}/auth/me`, { credentials: 'include' });
          if (res.status === 200) {
            // Auth visible — invalidate the react-query auth cache so components update
            try {
              await queryClient.invalidateQueries({ queryKey: authQueryKeys.me() });
            } catch {}
            stopped = true;
            return;
          }
        } catch {}

        if (!stopped && attempts < MAX_ATTEMPTS) {
          pollTimeoutId = setTimeout(() => void doPoll(), RETRY_INTERVAL_MS);
        }
      };

      void doPoll();

      return () => {
        stopped = true;
        if (pollTimeoutId !== null) {
          clearTimeout(pollTimeoutId);
          pollTimeoutId = null;
        }
      };
    }, [authData, postLoginCallback, queryClient]);

    // Use authData (from useSuspenseQuery) rather than user (from useAuthStatus)
    // to select the org. Both represent the same authenticated user, but
    // useAuthStatus runs an independent query that may still be pending when
    // authData has already resolved — causing the effect to silently no-op and
    // leaving selectedOrganization null, which stalls the redirect to /studies.
    useEffect(() => {
      if (data && data.length > 0 && authData) {
        const storedOrg = selectedOrgId ? data.find(org => org.id === selectedOrgId) : undefined;
        const orgToSelect = storedOrg ?? data[0];

        setTimeout(() => {
          try {
            const lockUntil = String(Date.now() + NAV_LOCK_DURATION_MS);
            sessionStorage.setItem('__nav_lock_until', lockUntil);
          } catch {}

          setSelectedOrg(orgToSelect);
          // Guard to avoid re-triggering this effect: selectedOrgId is in the
          // dependency array (needed to read the stored value), so writing it
          // unconditionally would cause a second run on every login.
          if (orgToSelect.id !== selectedOrgId) {
            setSelectedOrgId(orgToSelect.id);
          }
        }, 0);
      }
    }, [data, setSelectedOrg, setSelectedOrgId, authData, selectedOrgId]);

    const navigate = useNavigate();
    const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // If a selected organization exists, navigate to /studies.
    // Avoid returning <Navigate/> directly during render because a synchronous
    // navigation during React's commit can race with other effects and cause
    // nested updates. Instead, schedule a guarded navigation in an effect so
    // it runs after commit.
    useEffect(() => {
      if (selectedOrganization) {
        // Cancel any pending navigation from a previous org value so only the
        // latest selectedOrganization triggers the redirect. Without this, rapid
        // org changes before the setTimeout fires queue multiple navigate calls;
        // only the first passes canNavigateTo, leaving the user stuck on `/`.
        if (navTimerRef.current !== null) clearTimeout(navTimerRef.current);
        navTimerRef.current = setTimeout(() => {
          navTimerRef.current = null;
          void navigate({ to: '/studies' });
        }, 0);
      }
      return () => {
        if (navTimerRef.current !== null) {
          clearTimeout(navTimerRef.current);
          navTimerRef.current = null;
        }
      };
    }, [selectedOrganization, navigate]);

    // Navigation to /studies happens immediately; no need to show loading.
    // Let the /studies route handle its own loading state.
    if (selectedOrganization) {
      return null;
    }

    // Only show the orgs-loading spinner when the user IS authenticated —
    // isPending stays true indefinitely when the query is disabled (unauthenticated),
    // which would block the redirect to /login.
    if (authData && isPending) {
      return <FullLoading label={t('loading.default')} />;
    }

    // Authenticated user with no orgs — send to the no-org landing page
    if (authData !== null && data?.length === 0) {
      return <Navigate to="/no_org" />;
    }

    // Still waiting for the session cookie to propagate after a fresh login —
    // show a spinner rather than bouncing to /login prematurely.
    if (postLoginCallback && !authData) {
      return <FullLoading label={t('loading.default')} />;
    }

    // Authenticated with orgs loaded — selectedOrg is deferred by setTimeout(0) in the
    // org-selection effect. Return null to hold position without a loader flash while
    // Zustand updates; the navigation effect will send us to /studies next tick.
    if (authData && data && data.length > 0) {
      return null;
    }

    // Auth query still in-flight on a cold load and we have a prior-session hint
    // (selectedOrgId is persisted to localStorage by zustand only after a successful
    // login). Wait for /auth/me to resolve before deciding. For genuinely unauthenticated
    // contexts (no localStorage / no selectedOrgId), fall through to the /login redirect
    // below without a spinner — the auth query will resolve to null almost immediately
    // and we don't want to delay the bounce.
    if (authData === undefined && !error && selectedOrgId) {
      return <FullLoading label={t('loading.default')} />;
    }

    // error or not authenticated — navigate to login. The auth query will have already performed:
    return <Navigate to="/login" />;
  },
});
